import { createContext, useEffect, useState } from "react";
import { useStoredValue } from "../util";

const updateCss = (filename: string) => {
  const link = document.getElementById("theme-link") as HTMLLinkElement | null;

  const newHref = `${process.env.PUBLIC_URL}/${filename}`;
  const isSameTheme =
    (newHref.includes("dark") && link?.href.includes("dark")) ||
    (newHref.includes("light") && link?.href.includes("light"));
  if (link && !isSameTheme) {
    link.href = newHref;
  }
};

export type Theme = "light" | "dark";
export type ThemeMode = "light" | "dark" | "auto";
export const ThemeContext = createContext<{
  theme: Theme;
  setThemeOverride: (theme: Theme | undefined) => void;
  mode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
}>({
  theme: "light",
  setThemeOverride: () => {},
  mode: "auto",
  setThemeMode: () => {},
});

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { value: rawDarkMode, set: setDarkMode } =
    useStoredValue("dark-mode-setting");

  const darkMode: ThemeMode =
    rawDarkMode === "light" || rawDarkMode === "dark" || rawDarkMode === "auto"
      ? rawDarkMode
      : "auto";

  const [theme, setTheme] = useState<Theme>("dark");
  const [themeOverride, setThemeOverride] = useState<undefined | Theme>();

  const updateTheme = (theme: Theme) => {
    setTheme(theme);
    updateCss(
      theme === "dark"
        ? "themes/lara-dark-theme.css"
        : "themes/lara-light-theme.css"
    );
  };

  useEffect(() => {
    const computeTheme = (e: MediaQueryList | MediaQueryListEvent) => {
      if (themeOverride) {
        return themeOverride;
      }
      if (darkMode === "dark" || darkMode === "light") {
        return darkMode;
      }
      // auto
      return e.matches ? "dark" : "light";
    };

    const query = "(prefers-color-scheme: dark)";
    updateTheme(computeTheme(window.matchMedia(query)));

    const darkModeListener = (event: MediaQueryListEvent) => {
      updateTheme(computeTheme(event));
    };

    // Listen for changes to the system dark mode
    const darkModeMediaQuery = window.matchMedia(query);
    darkModeMediaQuery.addEventListener("change", darkModeListener);

    // Clean up by removing the listener when the component unmounts
    return () => {
      darkModeMediaQuery.removeEventListener("change", darkModeListener);
    };
  }, [themeOverride, darkMode]);

  return (
    <ThemeContext.Provider
      value={{
        theme: theme,
        setThemeOverride: setThemeOverride,
        mode: darkMode,
        setThemeMode: setDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
